import { useEffect, useRef, useState, useCallback } from "react";

interface UseIdleLogoutWithWarningOptions {
  logout: () => void;
  warningDuration?: number; // ms, default 30000
  idleDuration?: number; // ms, default 570000
}

export function useIdleLogoutWithWarning({
  logout,
  warningDuration = 30000,
  idleDuration = 570000,
}: UseIdleLogoutWithWarningOptions) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(warningDuration / 1000);
  const idleTimer = useRef<NodeJS.Timeout | null>(null);
  const countdownTimer = useRef<NodeJS.Timeout | null>(null);

  // Reset all timers and close modal
  const resetIdleTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (countdownTimer.current) clearInterval(countdownTimer.current);
    setShowWarning(false);
    setCountdown(warningDuration / 1000);
    idleTimer.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(warningDuration / 1000);
    }, idleDuration);
  }, [idleDuration, warningDuration]);

  // Set up event listeners for user activity
  useEffect(() => {
    const events = ["keydown", "mousedown"];
    const handleActivity = (e: Event) => {
      if (!showWarning) {
        resetIdleTimer();
      } else {
        setShowWarning(false);
        setCountdown(warningDuration / 1000);
        resetIdleTimer();
      }
    };
    events.forEach((event) => window.addEventListener(event, handleActivity));
    return () => {
      events.forEach((event) => window.removeEventListener(event, handleActivity));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (countdownTimer.current) clearInterval(countdownTimer.current);
    };
    // eslint-disable-next-line
  }, [resetIdleTimer]);

  // Only call resetIdleTimer on initial mount
  useEffect(() => {
    resetIdleTimer();
    // eslint-disable-next-line
  }, []);

  // Countdown logic
  useEffect(() => {
    if (!showWarning) return;
    countdownTimer.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimer.current!);
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (countdownTimer.current) clearInterval(countdownTimer.current);
    };
    // eslint-disable-next-line
  }, [showWarning, logout, warningDuration]);

  return { showWarning, countdown, setShowWarning };
} 